import { Link } from "react-router-dom";

type Pillar = {
  id: string;
  emoji: string;
  label: string;
  description: string;
  topics: string[];
  color: string;
};

const PILLARS: Pillar[] = [
  {
    id: "mind",
    emoji: "🧠",
    label: "Mind",
    description:
      "Mental clarity, emotional wellbeing, stress management, and the science of consciousness.",
    topics: [
      "Meditation",
      "Mindfulness",
      "Emotional Intelligence",
      "Dream Science",
      "Sleep Hygiene",
      "Focus",
    ],
    color: "from-blue-50 to-indigo-100 border-indigo-200",
  },
  {
    id: "body",
    emoji: "🌿",
    label: "Body",
    description:
      "Ayurveda, nutrition, movement, energy healing, and the body as a sacred temple.",
    topics: [
      "Ayurveda",
      "Yoga",
      "Nutrition",
      "Breathwork",
      "Herbal Medicine",
      "Energy Healing",
    ],
    color: "from-green-50 to-emerald-100 border-emerald-200",
  },
  {
    id: "spirit",
    emoji: "✨",
    label: "Spirit",
    description:
      "Awakening, consciousness, mysticism, prayer, and the direct experience of the divine.",
    topics: [
      "Awakening",
      "Consciousness",
      "Mysticism",
      "Bhakti",
      "Prayer",
      "Satsang",
    ],
    color: "from-amber-50 to-yellow-100 border-yellow-200",
  },
  {
    id: "relationships",
    emoji: "💞",
    label: "Relationships",
    description:
      "Conscious relating, community, soulmates, communication, and collective healing.",
    topics: [
      "Conscious Relationships",
      "Community",
      "Soulmate Inquiry",
      "Conflict Resolution",
      "Family Healing",
    ],
    color: "from-rose-50 to-pink-100 border-pink-200",
  },
  {
    id: "planet",
    emoji: "🌍",
    label: "Planet",
    description:
      "Sacred ecology, earth-based spirituality, activism, and living in harmony with nature.",
    topics: [
      "Sacred Ecology",
      "Earth Rituals",
      "Conscious Activism",
      "Plant Medicine",
      "Sustainability",
    ],
    color: "from-teal-50 to-cyan-100 border-cyan-200",
  },
];

const FEATURED_ARTICLES = [
  {
    pillar: "mind",
    title:
      "The Neuroscience of Meditation: What 10,000 Hours Does to Your Brain",
    author: "Master Bhargavi",
    read_time: 8,
  },
  {
    pillar: "spirit",
    title: "What Is Satsang? A Guide to Community as Spiritual Practice",
    author: "Master Aekam",
    read_time: 5,
  },
  {
    pillar: "body",
    title: "Your Dosha and Your Diet: An Introduction to Ayurvedic Nutrition",
    author: "Master Lakshmi Lavanya",
    read_time: 7,
  },
  {
    pillar: "relationships",
    title: "Five Agreements for Conscious Community Living",
    author: "OGGN Team",
    read_time: 6,
  },
  {
    pillar: "planet",
    title: "Honoring the Earth: Sacred Ecology in the Vedic Tradition",
    author: "OGGN Team",
    read_time: 5,
  },
];

const PILLAR_BY_ID: Record<string, Pillar> = Object.fromEntries(
  PILLARS.map((p) => [p.id, p]),
);

export default function WellnessPage() {
  return (
    <div className="cap-page">
      <section className="cap-hero">
        <div className="container">
          <div className="cap-hero__shell cap-hero__shell--split">
            <div>
              <span className="cap-hero__badge">Wellness Hub</span>
              <h1 className="display-1 cap-hero__title">
                Wellbeing across five dimensions
              </h1>
              <p className="cap-hero__subtitle">
                A holistic space for mind, body, spirit, relationships, and
                planet, rooted in ancient wisdom and informed by modern science.
              </p>
              <div className="hero-actions">
                <Link to="/courses" className="btn btn--gold">
                  Explore courses
                </Link>
                <Link to="/stories" className="btn btn--outline">
                  Read community stories
                </Link>
              </div>
            </div>

            <aside className="cap-hero__aside">
              <div className="cap-hero__card">
                <div className="cap-stat-grid">
                  <div className="cap-stat">
                    <span className="cap-stat__value">5</span>
                    <span className="cap-stat__label">pillars</span>
                  </div>
                  <div className="cap-stat">
                    <span className="cap-stat__value">24</span>
                    <span className="cap-stat__label">core topics</span>
                  </div>
                  <div className="cap-stat">
                    <span className="cap-stat__value">5</span>
                    <span className="cap-stat__label">featured articles</span>
                  </div>
                  <div className="cap-stat">
                    <span className="cap-stat__value">1</span>
                    <span className="cap-stat__label">practice path</span>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="section section--surface-alt">
        <div className="container">
          <div
            className="cap-grid cap-grid--teachers"
            style={{ marginBottom: "2rem" }}
          >
            {PILLARS.map((pillar) => (
              <div key={pillar.id} className="cap-card">
                <div className="cap-card__media" style={{ minHeight: "10rem" }}>
                  {pillar.emoji}
                </div>
                <div className="cap-card__body">
                  <span className="cap-pill cap-pill--indigo">
                    {pillar.label}
                  </span>
                  <p className="body-sm" style={{ color: "var(--c-ink-75)" }}>
                    {pillar.description}
                  </p>
                  <div className="cap-card__meta-row">
                    {pillar.topics.map((topic) => (
                      <span
                        key={topic}
                        className="cap-pill"
                        style={{
                          background: "rgba(61, 43, 31, 0.06)",
                          color: "var(--c-muted)",
                        }}
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <section className="cap-panel">
            <div className="cap-panel__header">
              <h2 className="heading-lg">Featured articles</h2>
            </div>
            <div className="cap-panel__body">
              <div className="cap-list">
                {FEATURED_ARTICLES.map((article) => {
                  const pillar = PILLAR_BY_ID[article.pillar];
                  return (
                    <div key={article.title} className="cap-list__item">
                      <span className="cap-list__index">{pillar.emoji}</span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <span
                          className="cap-pill cap-pill--indigo"
                          style={{ marginBottom: "0.35rem" }}
                        >
                          {pillar.label}
                        </span>
                        <h3 className="heading-md">{article.title}</h3>
                        <p className="text-muted body-sm">
                          By {article.author} · {article.read_time} min read
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section
            className="cap-panel"
            style={{
              marginTop: "1.5rem",
              background: "linear-gradient(135deg, #2f1f14, #5c3a18)",
            }}
          >
            <div
              className="cap-panel__body"
              style={{ color: "white", textAlign: "center" }}
            >
              <h2 className="display-3" style={{ color: "white" }}>
                Start your wellness journey
              </h2>
              <p
                style={{
                  color: "rgba(255,255,255,0.78)",
                  maxWidth: "40rem",
                  margin: "1rem auto 0",
                }}
              >
                Deepen your practice with structured courses, guided
                meditations, and community support.
              </p>
              <div
                className="hero-actions"
                style={{ justifyContent: "center", marginTop: "1.5rem" }}
              >
                <Link to="/courses" className="btn btn--gold">
                  Explore courses
                </Link>
                <Link
                  to="/stories"
                  className="btn btn--outline"
                  style={{
                    color: "white",
                    borderColor: "rgba(255,255,255,0.3)",
                  }}
                >
                  Read community stories
                </Link>
              </div>
            </div>
          </section>
        </div>
      </section>
    </div>
  );
}
